import { useEffect, useState } from 'react';
import {
  MdAdd,
  MdDelete,
  MdEdit,
  MdRemoveRedEye,
  MdSearch,
  MdToggleOff,
  MdToggleOn,
} from 'react-icons/md';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import { deleteUserAccount, subscribeToUsers, toggleUserStatus } from '../../services/userService';
import { CLASS_NAMES, USER_ROLES } from '../../utils/constants';
import UserFormModal from './UserFormModal';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToView, setUserToView] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Subscribe to real-time users data
  useEffect(() => {
    const unsubscribe = subscribeToUsers((usersData) => {
      setUsers(usersData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filter users by tab and search
  useEffect(() => {
    let filtered = users;

    if (activeTab !== 'all') {
      filtered = filtered.filter((user) => user.role === activeTab);
    }

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.fullName?.toLowerCase().includes(lowerSearch) ||
          user.email?.toLowerCase().includes(lowerSearch)
      );
    }

    setFilteredUsers(filtered);
  }, [users, activeTab, searchTerm]);

  const handleToggleStatus = async (user) => {
    try {
      await toggleUserStatus(user.id, user.isActive);
    } catch (error) {
      alert('Failed to update user status');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      await deleteUserAccount(userToDelete.id);
      setShowDeleteModal(false);
      setUserToDelete(null);
      alert('Success! The user was deleted successfully.');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete user';
      alert(`Error deleting user:\n\n${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setShowUserModal(true);
  };

  const handleViewUser = (user) => {
    setUserToView(user);
    setShowViewModal(true);
  };

  // Table columns
  const columns = [
    {
      header: 'Name',
      accessor: 'fullName',
      render: (value, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 min-w-[2.5rem] rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
            {value?.charAt(0) || 'U'}
          </div>
          <span className="font-medium text-gray-900">{value || 'N/A'}</span>
        </div>
      ),
    },
    {
      header: 'Role',
      accessor: 'role',
      render: (value) => {
        const colors = {
          student: 'bg-blue-100 text-blue-800',
          teacher: 'bg-emerald-100 text-emerald-800',
          editor: 'bg-purple-100 text-purple-800',
          admin: 'bg-amber-100 text-amber-800',
        };
        return (
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${colors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value?.charAt(0).toUpperCase() + value?.slice(1)}
          </span>
        );
      },
    },
    {
      header: 'Class',
      accessor: 'classLevel',
      render: (value) => (
        <span className="text-gray-700">{value ? CLASS_NAMES[value] : 'N/A'}</span>
      ),
    },
    {
      header: 'Subjects',
      accessor: 'assignedSubjects',
      render: (value) => (
        <span className="text-gray-600">{value?.length || 0} subjects</span>
      ),
    },
    {
      header: 'Status',
      accessor: 'isActive',
      render: (value) => (
        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleViewUser(row)}
            className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            title="View details"
          >
            <MdRemoveRedEye size={18} />
          </button>
        </div>
      ),
    },
  ];

  // Tabs
  const tabs = [
    { id: 'all', label: 'All Users', count: users.length },
    {
      id: USER_ROLES.STUDENT,
      label: 'Students',
      count: users.filter((u) => u.role === USER_ROLES.STUDENT).length,
    },
    {
      id: USER_ROLES.TEACHER,
      label: 'Teachers',
      count: users.filter((u) => u.role === USER_ROLES.TEACHER).length,
    },
    {
      id: USER_ROLES.ADMIN,
      label: 'Admins',
      count: users.filter((u) => u.role === USER_ROLES.ADMIN).length,
    },
    {
      id: USER_ROLES.EDITOR,
      label: 'Editors',
      count: users.filter((u) => u.role === USER_ROLES.EDITOR).length,
    },
  ];

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-darkNavy mb-1 md:mb-2 font-poppins">User Management</h1>
            <p className="text-sm md:text-base text-gray-500">View all users across the platform</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-4 md:mb-6">
        {/* Role Filter */}
        <select
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value)}
          className="px-3 md:px-4 py-2 md:py-2.5 border border-gray-300 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm md:text-base"
        >
          <option value="all">All Roles</option>
          <option value={USER_ROLES.STUDENT}>Students</option>
          <option value={USER_ROLES.TEACHER}>Teachers</option>
          <option value={USER_ROLES.EDITOR}>Editors</option>
          <option value={USER_ROLES.ADMIN}>Admins</option>
        </select>

        {/* Status Filter */}
        <select
          className="px-3 md:px-4 py-2 md:py-2.5 border border-gray-300 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm md:text-base"
        >
          <option value="all">All Users</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        {/* Search Bar */}
        <div className="relative flex-1">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 md:py-2.5 border border-gray-300 rounded-lg md:rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm md:text-base"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-sm text-gray-500 mb-0.5 md:mb-1">Total</p>
          <p className="text-base md:text-2xl font-bold text-darkNavy">{users.length}</p>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-sm text-gray-500 mb-0.5 md:mb-1">Students</p>
          <p className="text-base md:text-2xl font-bold text-darkNavy">
            {users.filter((u) => u.role === USER_ROLES.STUDENT).length}
          </p>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-sm text-gray-500 mb-0.5 md:mb-1">Teachers</p>
          <p className="text-base md:text-2xl font-bold text-darkNavy">
            {users.filter((u) => u.role === USER_ROLES.TEACHER).length}
          </p>
        </div>
        <div className="bg-white p-2 md:p-4 rounded-lg md:rounded-xl border border-gray-200">
          <p className="text-[10px] md:text-sm text-gray-500 mb-0.5 md:mb-1">Admins</p>
          <p className="text-base md:text-2xl font-bold text-darkNavy">
            {users.filter((u) => u.role === USER_ROLES.ADMIN).length}
          </p>
        </div>
      </div>

      {/* Loading/Empty States */}
      {loading ? (
        <div className="py-12 md:py-16 text-center text-gray-400 text-sm">Loading users...</div>
      ) : filteredUsers.length === 0 ? (
        <div className="py-12 md:py-16 text-center text-gray-400 text-sm">No users found</div>
      ) : (
        <>
          {/* Mobile Card List - Hidden on Desktop */}
          <div className="block lg:hidden space-y-3">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm"
              >
                {/* User Header with Avatar and View Button */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-12 h-12 min-w-[3rem] rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                      {user.fullName?.charAt(0) || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-base truncate">
                        {user.fullName || 'N/A'}
                      </h3>
                      <p className="text-sm text-gray-600 truncate">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewUser(user)}
                      className="p-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex-shrink-0"
                      title="View details"
                    >
                      <MdRemoveRedEye size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table - Hidden on Mobile */}
          <div className="hidden lg:block bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="overflow-x-auto">
              <DataTable columns={columns} data={filteredUsers} />
            </div>
          </div>
        </>
      )}

      {/* User Form Modal */}
      {showUserModal && (
        <UserFormModal
          user={selectedUser}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setUserToDelete(null);
        }}
        title="Delete User"
        size="small"
      >
        <div>
          <p className="mb-3 text-gray-700">
            Are you sure you want to delete{' '}
            <strong className="text-darkNavy">{userToDelete?.fullName}</strong>?
          </p>
          <p className="text-sm text-red-600 mb-6">This action cannot be undone.</p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setUserToDelete(null);
              }}
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleDeleteUser}
              disabled={isDeleting}
              className={`px-6 py-2.5 rounded-xl font-semibold transition-all hover:shadow-lg ${
                isDeleting ? 'bg-red-400 cursor-not-allowed text-white' : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {isDeleting ? 'Deleting...' : 'Delete User'}
            </button>
          </div>
        </div>
      </Modal>

      {/* View User Details Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setUserToView(null);
        }}
        title="User Details"
        size="medium"
      >
        {userToView && (
          <div className="space-y-6">
            {/* User Avatar and Name */}
            <div className="flex items-center gap-4 pb-6 border-b border-gray-200">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center text-white font-bold text-2xl">
                {userToView.fullName?.charAt(0) || 'U'}
              </div>
              <div>
                <h3 className="text-xl font-bold text-darkNavy">{userToView.fullName}</h3>
                <p className="text-gray-500">{userToView.email}</p>
              </div>
            </div>

            {/* User Information Grid */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">Role</label>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  userToView.role === 'student' ? 'bg-blue-100 text-blue-800' :
                  userToView.role === 'teacher' ? 'bg-emerald-100 text-emerald-800' :
                  'bg-amber-100 text-amber-800'
                }`}>
                  {userToView.role?.charAt(0).toUpperCase() + userToView.role?.slice(1)}
                </span>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">Status</label>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  userToView.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {userToView.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">Class Level</label>
                <p className="text-gray-900 font-medium">
                  {userToView.classLevel ? CLASS_NAMES[userToView.classLevel] : 'N/A'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-1">Joined Date</label>
                <p className="text-gray-900 font-medium">
                  {userToView.createdAt ? (
                    userToView.createdAt.toDate ? 
                      userToView.createdAt.toDate().toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }) : new Date(userToView.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                  ) : 'N/A'}
                </p>
              </div>
            </div>

            {/* Assigned Subjects */}
            {userToView.role === 'teacher' && (
              <div>
                <label className="block text-sm font-semibold text-gray-500 mb-2">Assigned Subjects</label>
                <div className="flex flex-wrap gap-2">
                  {userToView.assignedSubjects && userToView.assignedSubjects.length > 0 ? (
                    userToView.assignedSubjects.map((subject, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium"
                      >
                        {subject}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No subjects assigned</p>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setUserToView(null);
                }}
                className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Users;
